import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgePlus, Package, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { addonsApi } from '@/api/addons';
import { authApi } from '@/api/auth';
import { Button, EmptyState, Input, Loading, Modal } from '@/components/ui';
import { formatNIS } from '@/utils/currency';
import type { AddAddonParams, AddonCatalogItem, OrgAddon } from '@/types';

interface Props {
  orgId: string;
  orgName: string;
}

type ApprovalRole = AddAddonParams['approverRole'];
type BillingCycle = AddAddonParams['billingCycle'];
type Step = 1 | 2;

function getCatalogPriceForCycle(addon: AddonCatalogItem | null, cycle: BillingCycle): number | null {
  if (!addon) return null;
  if (cycle === 'monthly') return addon.price_monthly_agorot;
  if (cycle === 'yearly') return addon.price_yearly_agorot;
  return addon.price_onetime_agorot;
}

function computeMonthlyImpact(unitPriceAgorot: number | null, qty: number, cycle: BillingCycle): number {
  if (unitPriceAgorot === null) return 0;
  if (cycle === 'yearly') return Math.round((unitPriceAgorot * qty) / 12);
  return unitPriceAgorot * qty;
}

function computeDisplayTotal(unitPriceAgorot: number, qty: number, cycle: BillingCycle): string {
  const total = unitPriceAgorot * qty;
  if (cycle === 'yearly') return `${formatNIS(total)}/yr`;
  if (cycle === 'one_time') return `${formatNIS(total)} once`;
  return `${formatNIS(total)}/mo`;
}

function formatCatalogUnitPrice(addon: AddonCatalogItem, cycle: BillingCycle): string {
  const price = getCatalogPriceForCycle(addon, cycle);
  if (price === null) return 'Not available for selected cycle';
  if (cycle === 'yearly') return `${formatNIS(price)}/yr`;
  if (cycle === 'one_time') return `${formatNIS(price)} once`;
  return `${formatNIS(price)}/mo`;
}

function formatMonthlyTotal(addon: OrgAddon): string {
  const monthlyTotal = computeMonthlyImpact(addon.unit_price_agorot, addon.quantity, addon.billing_cycle);
  return formatNIS(monthlyTotal);
}

function buildApprovalText(monthlyImpactAgorot: number, cycle: BillingCycle): string {
  if (cycle === 'one_time') {
    return `This will add ${formatNIS(monthlyImpactAgorot)} as a one-time charge to this org's bill.`;
  }
  return `This will add ${formatNIS(monthlyImpactAgorot)} / month to this org's bill starting next cycle.`;
}

function getAdminDisplayName(firstName?: string | null, lastName?: string | null, email?: string | null): string {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return name || email || '';
}

function statusVariant(status: OrgAddon['status']): 'success' | 'warning' | 'neutral' {
  if (status === 'active') return 'success';
  if (status === 'pending_approval') return 'warning';
  return 'neutral';
}

interface AddAddonModalProps {
  isOpen: boolean;
  orgName: string;
  catalog: AddonCatalogItem[];
  onClose: () => void;
  onSubmit: (params: AddAddonParams) => void;
  isSaving: boolean;
}

function AddAddonModal({ isOpen, orgName, catalog, onClose, onSubmit, isSaving }: AddAddonModalProps) {
  const [step, setStep] = useState<Step>(1);
  const [addonCode, setAddonCode] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [approverName, setApproverName] = useState('');
  const [signature, setSignature] = useState('');
  const [approverRole, setApproverRole] = useState<ApprovalRole>('admin');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: me } = useQuery({
    queryKey: ['auth-me'],
    queryFn: authApi.me,
    enabled: isOpen,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setAddonCode('');
    setQuantity('1');
    setBillingCycle('monthly');
    setSignature('');
    setApproverRole('admin');
    setErrors({});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setApproverName(getAdminDisplayName(me?.first_name, me?.last_name, me?.email));
  }, [isOpen, me]);

  const selectedAddon = useMemo(
    () => catalog.find(item => item.code === addonCode) ?? null,
    [catalog, addonCode]
  );

  const qty = Math.max(1, Number(quantity || 1));
  const unitPrice = getCatalogPriceForCycle(selectedAddon, billingCycle);
  const monthlyImpact = computeMonthlyImpact(unitPrice, qty, billingCycle);
  const canContinue = !!selectedAddon && unitPrice !== null && qty > 0;

  const validateStepOne = () => {
    const nextErrors: Record<string, string> = {};
    if (!selectedAddon) nextErrors.addon = 'Select an add-on';
    if (!quantity || Number.isNaN(Number(quantity)) || Number(quantity) <= 0) nextErrors.quantity = 'Quantity must be greater than 0';
    if (unitPrice === null) nextErrors.billingCycle = 'Selected add-on does not support this billing cycle';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStepTwo = () => {
    const nextErrors: Record<string, string> = {};
    if (!approverName.trim()) nextErrors.approverName = 'Approver name is required';
    if (!signature.trim()) nextErrors.signature = 'Signature is required';
    if (!['admin', 'owner'].includes(approverRole)) nextErrors.approverRole = 'Approver role must be admin or owner';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStepOne()) return;
    setStep(2);
  };

  const handleSubmit = () => {
    if (!validateStepTwo() || !selectedAddon) return;
    onSubmit({
      addonCode: selectedAddon.code,
      quantity: qty,
      billingCycle,
      approverName: approverName.trim(),
      approverRole,
      signature: signature.trim(),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Add add-on - ${orgName}`}
      size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>Cancel</Button>
          {step === 2 && (
            <Button variant="secondary" onClick={() => setStep(1)} disabled={isSaving}>Back</Button>
          )}
          {step === 1 ? (
            <Button onClick={handleNext} disabled={!canContinue}>Next</Button>
          ) : (
            <Button onClick={handleSubmit} loading={isSaving}>Approve & Add</Button>
          )}
        </>
      )}
    >
      {step === 1 ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1.5">Add-on</label>
            <select
              value={addonCode}
              onChange={e => {
                setAddonCode(e.target.value);
                setErrors(prev => ({ ...prev, addon: '', billingCycle: '' }));
              }}
              className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary"
            >
              <option value="">Select add-on</option>
              {catalog.map(item => (
                <option key={item.code} value={item.code}>
                  {item.name} ({item.code})
                </option>
              ))}
            </select>
            {errors.addon && <p className="mt-1 text-xs text-danger">{errors.addon}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Quantity"
              type="number"
              min="1"
              step="1"
              value={quantity}
              onChange={e => {
                setQuantity(e.target.value);
                setErrors(prev => ({ ...prev, quantity: '' }));
              }}
              error={errors.quantity}
            />

            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1.5">Billing cycle</label>
              <select
                value={billingCycle}
                onChange={e => {
                  setBillingCycle(e.target.value as BillingCycle);
                  setErrors(prev => ({ ...prev, billingCycle: '' }));
                }}
                className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary"
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="one_time">One-time</option>
              </select>
              {errors.billingCycle && <p className="mt-1 text-xs text-danger">{errors.billingCycle}</p>}
            </div>
          </div>

          {selectedAddon && (
            <div className="rounded-xl border border-navy-border bg-navy-dark p-4 space-y-1">
              <p className="text-sm text-ink-primary font-medium">{selectedAddon.name}</p>
              <p className="text-xs text-ink-secondary">
                Unit price: {formatCatalogUnitPrice(selectedAddon, billingCycle)}
              </p>
              <p className="text-xs text-ink-secondary">
                Preview: {unitPrice === null ? '-' : computeDisplayTotal(unitPrice, qty, billingCycle)}
              </p>
              <p className="text-xs text-ink-muted">
                Monthly billing impact: {formatNIS(monthlyImpact)}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-gold/30 bg-gold/10 p-4 text-sm text-ink-primary">
            {buildApprovalText(monthlyImpact, billingCycle)}
          </div>

          <Input
            label="Approver name"
            value={approverName}
            onChange={e => {
              setApproverName(e.target.value);
              setErrors(prev => ({ ...prev, approverName: '' }));
            }}
            error={errors.approverName}
          />

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1.5">Approver role</label>
            <div className="flex gap-4">
              {(['admin', 'owner'] as ApprovalRole[]).map(role => (
                <label key={role} className="inline-flex items-center gap-2 text-sm text-ink-primary">
                  <input
                    type="radio"
                    name="approver-role"
                    checked={approverRole === role}
                    onChange={() => {
                      setApproverRole(role);
                      setErrors(prev => ({ ...prev, approverRole: '' }));
                    }}
                  />
                  {role}
                </label>
              ))}
            </div>
            {errors.approverRole && <p className="mt-1 text-xs text-danger">{errors.approverRole}</p>}
          </div>

          <Input
            label="Typed signature"
            value={signature}
            onChange={e => {
              setSignature(e.target.value);
              setErrors(prev => ({ ...prev, signature: '' }));
            }}
            error={errors.signature}
            hint="Type the approver's signature to confirm approval."
          />
        </div>
      )}
    </Modal>
  );
}

export default function OrgAddonsTab({ orgId, orgName }: Props) {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);

  const { data: orgAddons, isLoading } = useQuery({
    queryKey: ['org-addons', orgId],
    queryFn: () => addonsApi.listForOrg(orgId),
  });

  const { data: catalog = [], isLoading: catalogLoading } = useQuery({
    queryKey: ['addon-catalog'],
    queryFn: () => addonsApi.listCatalog(),
    staleTime: 60_000,
  });

  const addMutation = useMutation({
    mutationFn: (params: AddAddonParams) => addonsApi.addForOrg(orgId, params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-addons', orgId] });
      toast.success('Add-on approved and added');
      setShowModal(false);
    },
    onError: (error: any) => toast.error(error.message ?? 'Failed to add add-on'),
  });

  const cancelMutation = useMutation({
    mutationFn: addonsApi.cancel,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-addons', orgId] });
      toast.success('Add-on cancelled');
    },
    onError: (error: any) => toast.error(error.message ?? 'Failed to cancel add-on'),
  });

  if (isLoading || catalogLoading) {
    return <Loading text="Loading add-ons..." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowModal(true)}>
          <BadgePlus className="h-4 w-4" />
          Add add-on
        </Button>
      </div>

      {orgAddons?.length ? (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy-border text-ink-muted">
                  {['Add-on', 'Qty', 'Unit price', 'Monthly total', 'Cycle', 'Status', 'Actions'].map(header => (
                    <th key={header} className="text-left p-3 font-medium">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orgAddons.map(addon => (
                  <tr key={addon.id} className="border-b border-navy-border/70 last:border-b-0">
                    <td className="p-3">
                      <div className="text-ink-primary font-medium">
                        {addon.addon_catalog?.name ?? addon.addon_code}
                      </div>
                      <div className="text-xs text-ink-muted">{addon.addon_code}</div>
                    </td>
                    <td className="p-3 text-ink-primary">{addon.quantity}</td>
                    <td className="p-3 text-ink-secondary">{formatNIS(addon.unit_price_agorot)}</td>
                    <td className="p-3 text-ink-secondary">{formatMonthlyTotal(addon)}</td>
                    <td className="p-3 text-ink-secondary capitalize">{addon.billing_cycle.replace('_', ' ')}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center">
                        <Button variant="ghost" size="sm" className="pointer-events-none">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs ${
                            statusVariant(addon.status) === 'success'
                              ? 'bg-teal/10 text-teal'
                              : statusVariant(addon.status) === 'warning'
                                ? 'bg-amber-500/10 text-amber-400'
                                : 'bg-navy-hover text-ink-secondary'
                          }`}>
                            {addon.status}
                          </span>
                        </Button>
                      </span>
                    </td>
                    <td className="p-3">
                      {addon.status === 'active' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={cancelMutation.isPending}
                          onClick={() => cancelMutation.mutate(addon.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Cancel
                        </Button>
                      ) : (
                        <span className="text-xs text-ink-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EmptyState
          icon={<Package className="h-8 w-8" />}
          title="No add-ons"
          description="No add-ons have been assigned to this organization yet."
        />
      )}

      <AddAddonModal
        isOpen={showModal}
        orgName={orgName}
        catalog={catalog}
        onClose={() => setShowModal(false)}
        onSubmit={(params) => addMutation.mutate(params)}
        isSaving={addMutation.isPending}
      />
    </div>
  );
}
